// Lock joining + age helpers for the v2 dashboard (DEVOPS-1633).
//
// Locks live in a separate table keyed by job_key = `{app_name}#{type}:{job_id}`
// and env_name. A deployment row joins to its lock by rebuilding that same key.

import { DeploymentRecord, LockInfo } from '../../types/DeploymentRecord';

/**
 * Rollback locks older than this get visual escalation so operators can't
 * ignore a stale rollback lock (the bad deploy it guards still needs attention).
 * Rollback locks are placed for 7 days; a day untouched is the forcing point.
 */
export const ROLLBACK_LOCK_ESCALATION_MS = 24 * 60 * 60 * 1000;

/** Composite lookup key for a (job_key, env) pair. */
export const lockMapKey = (jobKey: string, envName: string): string =>
  `${jobKey}::${envName}`;

/**
 * The lock-map key a deployment record would match, or null if the record lacks
 * the identifying fields. Mirrors the backend `build_job_key`
 * (`{app_name}#{type}:{job_id}`) plus the env.
 */
export const recordLockKey = (record: DeploymentRecord): string | null => {
  const app = record.app || record.app_name;
  const job = record.job_id || record.serviceName;
  const env = record.envName || record.env;
  if (!app || !job || !env) return null;
  return lockMapKey(`${app}#${record.type}:${job}`, env);
};

/** Elapsed ms since the lock was placed, or null if locked_at is missing/bad. */
export const lockAgeMs = (lock: LockInfo, now: number): number | null => {
  if (!lock.locked_at) return null;
  const t = Date.parse(lock.locked_at);
  if (Number.isNaN(t)) return null;
  return now - t;
};

/** True when a rollback-source lock has aged past the escalation threshold. */
export const isEscalatedRollbackLock = (lock: LockInfo, now: number): boolean => {
  if (lock.lock_source !== 'rollback') return false;
  const age = lockAgeMs(lock, now);
  return age !== null && age >= ROLLBACK_LOCK_ESCALATION_MS;
};
